import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { graphql } from '~/apollo/generated/v3';
import { DepositionalWikiType } from '~/apollo/generated/v3/graphql';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { uploadGeologyUpdateRoute } from '~/paths';
import { isEnumMember } from '~/utils/common';
import { useZodForm } from '~/utils/forms';
import type { DepositionalWikiFormValues } from '~/utils/modules/depositionalWiki';
import {
  depositionalWikiSchema,
  initialDepositionalWiki,
} from '~/utils/modules/depositionalWiki';
import { DepositionalWikiFormFields } from './depositional-wiki-form-fields';

export const CREATE_DEPOSITIONAL_WIKI = graphql(`
  mutation CreateDepositionalWiki($depositional: DepositionalWikiInput!) {
    createDepositionalWiki(depositional: $depositional) {
      ...depositionalWikiParts
    }
  }
`);

function initialValues(sp: URLSearchParams): DepositionalWikiFormValues {
  const values = initialDepositionalWiki();

  const geologyType = sp.get('geologyType');
  if (geologyType) {
    values.geologyType = geologyType;
  }

  const typeValue = sp.get('type');
  if (typeValue && isEnumMember(DepositionalWikiType, typeValue)) {
    values.type = typeValue;
  }

  const value = sp.get('value');
  if (value) {
    values.value = [value];
  }

  return values;
}

export function CreateDepositionalWikiForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [createWikiPage, { loading, error }] = useMutation(
    CREATE_DEPOSITIONAL_WIKI,
  );

  const form = useZodForm({
    schema: depositionalWikiSchema,
    values: initialValues(searchParams),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      const result = await createWikiPage({
        variables: { depositional: values },
      });
      const wikiPage = result.data?.createDepositionalWiki;
      invariant(wikiPage, 'Error parsing wikiPage from response!');
      toast.success('Wiki page saved successfully.');
      navigate(uploadGeologyUpdateRoute(wikiPage.id));
    } catch (err) {
      console.log('Error saving wiki page', err);
      toast.error('There was a problem saving the wiki page.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <DepositionalWikiFormFields />
          <HookFormErrors graphQLError={error} />

          <div className="text-center">
            <Button type="submit" color="primary" loading={loading}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
